import { createClient } from 'next-sanity'
import { groq } from 'next-sanity'
import imageUrlBuilder from '@sanity/image-url'
import { projectId, dataset, apiVersion, token } from '@/sanity/env'
import logger from './logger'

export const client = createClient({
  projectId,
  dataset,
  apiVersion,
  useCdn: false,
  perspective: 'published',
  token,
  ignoreBrowserTokenWarning: true,
  stega: false
})

// Create an image URL builder
const builder = imageUrlBuilder(client)

// Default image to use when no image is available
export const DEFAULT_IMAGE = 'https://placehold.co/800x600/f5f5f5/666666.png'

// Helper function to get image URL with fallback
export const getImageUrl = (image) => {
  try {
    if (!image) {
      return DEFAULT_IMAGE;
    }

    if (typeof image === 'string') {
      if (image.startsWith('https://')) {
        return image;
      }
      return DEFAULT_IMAGE;
    }

    const url = builder.image(image).url();
    return url || DEFAULT_IMAGE;
  } catch (error) {
    logger.error('Error building image URL:', { error, imageData: image });
    return DEFAULT_IMAGE;
  }
};

// Helper function to transform the data
const transformData = (data) => {
  if (!data) return {};

  const transformItems = (items) => {
    if (!items || !Array.isArray(items)) return [];
    return items.map(item => ({
      ...item,
      imageUrl: item.image ? getImageUrl(item.image) : DEFAULT_IMAGE,
      addons: item.addons || [] // Preserve addons array
    }));
  };

  return {
    mochiDonuts: transformItems(data.mochiDonuts),
    koreanCorndog: transformItems(data.koreanCorndog),
    drinks: transformItems(data.drinks),
    bobaTea: transformItems(data.bobaTea),
    entrees: transformItems(data.entrees),
    desserts: transformItems(data.desserts)
  };
};

export async function getMenuData() {
  try {
    const query = `{
      "mochiDonuts": *[_type == "mochiDonut" && !(_id in path("drafts.**"))] | order(order asc) {
        _id, name, description, prices, image, available, isPopular, onSale, order, _updatedAt
      },
      "koreanCorndog": *[_type == "koreanCorndog" && !(_id in path("drafts.**"))] | order(order asc) {
        _id, 
        name, 
        description, 
        price, 
        image, 
        available, 
        isPopular, 
        onSale, 
        type,
        order,
        addons[] {
          name,
          price,
          description
        },
        _updatedAt
      },
      "drinks": *[_type == "drinks" && !(_id in path("drafts.**"))] | order(order asc) {
        _id, 
        name, 
        description, 
        image, 
        available, 
        isPopular, 
        onSale,
        subCategory, 
        order,
        sizes[] {
          size,
          price
        }, 
        flavorOptions,
        coffeeSize,
        coffeePrice,
        coffeeAddons[] {
          name,
          price,
          description
        },
        standardPrice,
        _updatedAt
      },
      "bobaTea": *[_type == "bobaTea" && !(_id in path("drafts.**"))] | order(order asc) {
        _id, name, description, image, available, isPopular, onSale,
        sizes, sugarOptions, iceOptions, price, order, _updatedAt
      },
      "entrees": *[_type == "entrees" && !(_id in path("drafts.**"))] | order(order asc) {
        _id, 
        name, 
        description, 
        price, 
        image, 
        available, 
        isPopular, 
        onSale,
        category,
        order,
        type,
        ...select(category != 'musubi' => {
          pieces,
          sizes
        }, {}),
        _updatedAt
      },
      "desserts": *[_type == "desserts" && !(_id in path("drafts.**"))] | order(order asc) {
        _id, 
        name, 
        description, 
        image, 
        available, 
        isPopular, 
        onSale,
        type,
        price,
        order,
        addons[] {
          name,
          price,
          description
        },
        _updatedAt
      }
    }`;

    const data = await client.fetch(query);
    return transformData(data);
  } catch (error) {
    console.error('Error fetching menu data:', error);
    return {
      mochiDonuts: [],
      koreanCorndog: [],
      drinks: [],
      bobaTea: [],
      entrees: [],
      desserts: []
    };
  }
}

export async function getSiteSettings() {
  try {
    logger.info('Fetching site settings from Sanity...')
    const query = groq`*[_type == "siteSettings"][0] {
      testimonials[] {
        name,
        role,
        text,
        rating,
        "imageUrl": image.asset->url
      }
    }`

    const data = await client.fetch(query, {}, {
      cache: 'no-store',
      next: { revalidate: 0 }
    })

    return {
      ...data,
      testimonials: data?.testimonials?.map(testimonial => ({
        ...testimonial,
        imageUrl: testimonial.imageUrl || DEFAULT_IMAGE
      })) || []
    }
  } catch (error) {
    logger.error('Error fetching site settings:', { error })
    return {
      testimonials: []
    }
  }
}

export async function getSpecials() {
  const query = `*[_type == "specials" && active == true && dateTime(endDate) > dateTime(now())] | order(order asc) {
    _id,
    name,
    description,
    displayType,
    "imageUrl": image.asset->url,
    originalPrice,
    discountedPrice,
    endDate
  }`;
  
  return await client.fetch(query);
}

export async function getAboutContent() {
  try {
    const query = `*[_type == "about"][0] {
      heroSection {
        title,
        subtitle,
        description
      },
      storySection1 {
        title,
        content,
        "imageUrl": image.asset->url
      },
      menuSection {
        title,
        content,
        "imageUrl": image.asset->url,
        menuItems[] {
          icon,
          title,
          description,
          "imageUrl": image.asset->url
        }
      },
      storySection3 {
        title,
        content,
        "imageUrl": image.asset->url
      },
      valuesSection {
        title,
        subtitle,
        description,
        values[] {
          icon,
          title,
          description
        }
      },
      locationSection {
        title,
        address,
        hoursTitle,
        hours
      }
    }`;
    
    const data = await client.fetch(query);
    return data;
  } catch (error) {
    console.error('Error fetching about content:', error);
    return null;
  }
} 